import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { clockNow, useClock, useUI } from '@/state/store';
import { TextButton } from '@/components/ui/primitives';
import { registerMissionTwin } from '@/features/twin/data';
import { dive } from '@/features/dive/controller';
import { createDiveDeps } from '@/features/dive/deps';
import { missionTle, pointingFor } from '@/features/dive/pointing';
import { useLoader } from '../store';
import { getOrbitEngine } from '../engine/OrbitEngine';
import { tleEpochAgeDays } from '../globe/geometry';
import { getGlobeEngine } from '../globe/engineHandle';
import type { ContactWindow } from '../types';
import { usePass, passPhase } from './store';
import { elevationSamples, explainLink } from './geometry';
import { StationWeather } from '../ui/panels/StationWeather';
import { usePresentation } from '../ui/presentation';
import { toast } from '@/components/ui/toast';
import { spacecraftModelFor, SPACECRAFT_CATALOG } from '../globe/spacecraftCatalog';
import { usePassNow } from './usePassNow';

const selectClass = 'w-full rounded-control border border-hairline bg-inset px-2 py-1.5 text-xs text-ink';
const utc = (ms: number) => new Date(ms).toISOString().slice(11, 19) + ' UTC';
export function PassInspector() {
  const navigate = useNavigate();
  const dataset = useLoader((s) => s.dataset);
  const results = useLoader((s) => s.results);
  const activeContacts = useLoader((s) => s.activeContacts);
  const selection = useUI((s) => s.selection);
  const selected = usePass((s) => s.selected);
  const now = usePassNow();
  const eventFollowing = usePresentation((s) => s.eventFollowing);
  const mission = dataset?.missions.find((m) => m.id === selection.missionId);
  const antenna = dataset?.antennas.find((a) => a.id === selection.antennaId);
  const [scan, setScan] = useState<{
    key: string;
    windows: ContactWindow[];
    error?: string;
    boundaryVisibility?: boolean;
  }>({ key: '', windows: [] });
  const pairKey = `${dataset?.network}:${mission?.id}:${antenna?.id}`;
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!mission || !antenna) return;
    const tle = missionTle(mission);
    if (!tle) return;
    let cancelled = false;
    const start = clockNow() - 30 * 60_000;
    // Visibility scan, independent of allocation and weather-shortened planning windows.
    void getOrbitEngine()
      .computeContactWindows({
        visibilityOnly: true,
        missions: [{ id: mission.id, tle1: tle.line1, tle2: tle.line2 }],
        antennas: [
          { id: antenna.id, ...antenna.location, elevationMinDeg: antenna.coverage.elevationMinDeg ?? 5 },
        ],
        startMs: start,
        endMs: start + 24 * 3_600_000,
        stepSeconds: 10,
      })
      .then((windows) => {
        const found = windows[mission.id]?.[antenna.id] ?? [];
        const complete = found.filter((w) => w.startMs > start && w.endMs < start + 24 * 3_600_000);
        if (!cancelled)
          setScan({ key: pairKey, windows: complete, boundaryVisibility: complete.length !== found.length });
      })
      .catch((e: unknown) => {
        if (!cancelled) setScan({ key: pairKey, windows: [], error: String(e) });
      });
    return () => {
      cancelled = true;
    };
  }, [mission, antenna, pairKey, revision]);
  const pass = selected?.missionId === mission?.id && selected?.antennaId === antenna?.id ? selected : null;
  const samples = useMemo(
    () => (mission && antenna && pass ? elevationSamples(mission, antenna, pass) : []),
    [mission, antenna, pass],
  );
  const peak = samples.reduce(
    (a, b) => (b.elevation > a.elevation ? b : a),
    samples[0] ?? { timeMs: 0, elevation: 0 },
  );
  const look = mission && antenna ? pointingFor(antenna, mission, now) : null;
  const eligible = activeContacts.some((c) => c.missionId === mission?.id && c.antennaId === antenna?.id);
  const scheduled =
    mission &&
    antenna &&
    results?.allocations[mission.id]?.[antenna.id]?.some((p) => p.startMs <= now && now < p.endMs);
  const reasons = mission && antenna ? explainLink(mission, antenna, now, results) : [];
  const model = mission ? spacecraftModelFor(mission) : undefined;
  const choose = (missionId: string | null, antennaId: string | null) => {
    usePass.getState().select(null);
    useUI.getState().select({ missionId, antennaId, assetId: null });
  };
  const scrub = (ms: number) => useClock.setState({ live: false, playing: false, simTime: ms });
  return (
    <section className="flex flex-col gap-3 p-4 text-xs" data-testid="pass-inspector">
      <h2 className="font-semibold text-ink">Pass inspector</h2>
      <details className="text-ink-2">
        <summary className="cursor-pointer">Spacecraft model catalogue</summary>
        {SPACECRAFT_CATALOG.map((item) => (
          <p className="mt-2 text-[11px]" key={item.family}>
            <strong>{item.family}</strong> · {item.status.replaceAll('-', ' ')}
            <br />
            {item.note}
          </p>
        ))}
      </details>
      <label className="text-ink-3">
        Spacecraft
        <select
          aria-label="Pass spacecraft"
          className={selectClass}
          value={mission?.id ?? ''}
          onChange={(e) => choose(e.target.value || null, antenna?.id ?? null)}
        >
          <option value="">Choose spacecraft</option>
          {dataset?.missions.map((m) => (
            <option key={m.id} value={m.id}>
              {m.shortName || m.name}
            </option>
          ))}
        </select>
      </label>
      <label className="text-ink-3">
        Receiving antenna
        <select
          aria-label="Pass antenna"
          className={selectClass}
          value={antenna?.id ?? ''}
          onChange={(e) => choose(mission?.id ?? null, e.target.value || null)}
        >
          <option value="">Choose antenna</option>
          {dataset?.antennas.map((a) => (
            <option key={a.id} value={a.id}>
              {a.location.name} · {a.shortName}
            </option>
          ))}
        </select>
      </label>
      <TextButton
        onClick={() => {
          const m = dataset?.missions.find((m) => String(m.noradId) === '41240');
          const a =
            dataset?.antennas.find((a) => a.id === 'wallops_26m') ??
            dataset?.antennas.find((a) => /wallops/i.test(a.location.name ?? ''));
          if (m && a) choose(m.id, a.id);
          else toast('info', 'Choose NOAA NESDIS to inspect Jason-3 at Wallops.');
        }}
      >
        Jason-3 / Wallops
      </TextButton>
      {mission && antenna && (
        <>
          <p className="text-ink-2">
            {antenna.location.name} ·{' '}
            {mission.requirements.bandType ??
              mission.requirements.bandTypes?.join(', ') ??
              'Band unspecified'}{' '}
            · minimum {antenna.coverage.elevationMinDeg ?? 5}°
          </p>
          <div
            className="grid grid-cols-2 gap-1 rounded-control border border-hairline p-2 text-[11px]"
            aria-label="Contact state"
          >
            <span>Geometrically visible</span>
            <strong>{look ? (look.elevationDeg >= 0 ? 'Yes' : 'No') : 'Unknown'}</strong>
            <span>Eligible live prediction</span>
            <strong>{eligible ? 'Yes' : 'No'}</strong>
            <span>Planning allocation</span>
            <strong>{scheduled ? 'Scheduled' : 'Not scheduled'}</strong>
            <span>Confirmed receiving</span>
            <strong>Telemetry unavailable</strong>
          </div>
          <details>
            <summary className="cursor-pointer text-accent">Explain this link</summary>
            <ul className="mt-2 list-disc space-y-1 pl-4 text-ink-2">
              {(reasons.length
                ? reasons
                : [
                    'No exclusion found in the displayed constraints. Eligibility comes from the live contact engine; allocation and receiver confirmation are separate.',
                  ]
              ).map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </details>
          <p className="text-[10px] text-ink-3">
            {missionTle(mission)
              ? `TLE age ${Math.abs(tleEpochAgeDays(missionTle(mission)!.line1, now)).toFixed(1)} days at the displayed time`
              : 'TLE unavailable'}{' '}
            · network source: {dataset?.source}. All passes are predictions.
          </p>
          <p className="text-[10px] text-ink-3">
            {model?.note ??
              'No verified model for this spacecraft. The orbital marker preserves its calculated position.'}
          </p>
          <label className="text-ink-3">
            Visibility passes (UTC)
            <select
              aria-label="Visibility pass"
              className={selectClass}
              value={pass?.startMs ?? ''}
              onChange={(e) => {
                const w = scan.windows.find((p) => p.startMs === Number(e.target.value));
                if (w) usePass.getState().select({ ...w, missionId: mission.id, antennaId: antenna.id });
              }}
            >
              <option value="">
                {!missionTle(mission)
                  ? 'Orbital elements unavailable for this spacecraft'
                  : scan.key !== pairKey
                    ? 'Calculating passes…'
                    : scan.windows.length
                      ? 'Choose a pass to inspect'
                      : scan.boundaryVisibility
                        ? 'Visibility crosses the scan boundary'
                        : 'No pass in this 24-hour scan'}
              </option>
              {pass && !scan.windows.some((p) => p.startMs === pass.startMs) && (
                <option value={pass.startMs}>Selected pass · {utc(pass.startMs)}</option>
              )}
              {scan.key === pairKey &&
                scan.windows.map((p) => (
                  <option key={p.startMs} value={p.startMs}>
                    {new Date(p.startMs).toISOString().slice(0, 10)} {utc(p.startMs)} ·{' '}
                    {p.durationMin.toFixed(1)} min
                  </option>
                ))}
            </select>
          </label>
          {scan.key === pairKey && scan.boundaryVisibility && (
            <p className="text-[10px] text-ink-3">
              Visibility also extends beyond this scan. Only passes with both acquisition and loss inside the
              scan are listed; boundary times are not presented as AOS/LOS.
            </p>
          )}
          {scan.key === pairKey && scan.error && <p role="alert">Pass scan failed: {scan.error}</p>}
          <TextButton onClick={() => setRevision((n) => n + 1)}>Find passes from current time</TextButton>
          {pass && (
            <>
              <div className="rounded-control border border-hairline p-2" data-testid="pass-times">
                <strong>{passPhase(pass, now)}</strong>
                <p>
                  AOS {utc(pass.startMs)} · peak ≈ {utc(peak.timeMs)} · LOS {utc(pass.endMs)}
                </p>
                <p>
                  {((pass.endMs - pass.startMs) / 60_000).toFixed(1)} min · peak {peak.elevation.toFixed(1)}°
                  geometric elevation
                </p>
                <p>
                  {now < pass.startMs
                    ? `Acquisition in ${Math.ceil((pass.startMs - now) / 1000)} s`
                    : now < pass.endMs
                      ? `${Math.ceil((pass.endMs - now) / 1000)} s remaining`
                      : 'Pass ended; select the next opportunity above.'}
                </p>
              </div>
              <svg
                viewBox="0 0 300 112"
                className="w-full rounded-control bg-inset"
                role="img"
                aria-label="Geometric elevation versus time"
              >
                <text x="4" y="12" fill="currentColor" fontSize="9">
                  90°
                </text>
                <text x="4" y="98" fill="currentColor" fontSize="9">
                  0°
                </text>
                <line x1="25" x2="295" y1="96" y2="96" stroke="currentColor" opacity="0.3" />
                <polyline
                  points={samples
                    .map(
                      (p) =>
                        `${25 + (270 * (p.timeMs - pass.startMs)) / (pass.endMs - pass.startMs)},${96 - Math.max(0, p.elevation)}`,
                    )
                    .join(' ')}
                  fill="none"
                  stroke="var(--color-accent, #c5da68)"
                  strokeWidth="2"
                />
                <line
                  x1={25 + 270 * Math.max(0, Math.min(1, (now - pass.startMs) / (pass.endMs - pass.startMs)))}
                  x2={25 + 270 * Math.max(0, Math.min(1, (now - pass.startMs) / (pass.endMs - pass.startMs)))}
                  y1="6"
                  y2="96"
                  stroke="currentColor"
                />
              </svg>
              <input
                aria-label="Scrub pass time"
                type="range"
                min={-30}
                max={(pass.endMs - pass.startMs) / 1000 + 30}
                step={1}
                value={Math.max(
                  -30,
                  Math.min((pass.endMs - pass.startMs) / 1000 + 30, (now - pass.startMs) / 1000),
                )}
                onChange={(e) => scrub(pass.startMs + Number(e.target.value) * 1000)}
                onKeyDown={(e) => {
                  if (e.key === 'Home' || e.key === 'End') {
                    e.preventDefault();
                    e.stopPropagation();
                    scrub(e.key === 'Home' ? pass.startMs - 30_000 : pass.endMs + 30_000);
                  }
                }}
              />
              <div className="flex flex-wrap gap-1">
                <TextButton onClick={() => usePass.getState().replay()}>Replay pass</TextButton>
                <TextButton onClick={() => scrub(peak.timeMs)}>Go to peak</TextButton>
                <TextButton
                  onClick={() => {
                    usePass.setState({ replaying: false });
                    useClock.getState().setLive();
                  }}
                >
                  Return to live
                </TextButton>
              </div>
              <p className="text-[10px] text-ink-3">
                AOS/LOS use the existing elevation scan at 10-second resolution. Allocation, weather, terrain
                and tracking constraints can further restrict availability.
              </p>
            </>
          )}
          <div className="flex flex-wrap gap-1">
            {model?.status === 'available' && (
              <TextButton
                onClick={() => {
                  useLoader.getState().setView({ sceneMode: '3D' });
                  useLoader.getState().setTracked(mission.id);
                  getGlobeEngine()?.inspectMission?.(mission.id);
                }}
              >
                Inspect model in orbit
              </TextButton>
            )}
            <TextButton
              onClick={() => {
                useLoader.getState().setView({ sceneMode: '3D' });
                useLoader.getState().setTracked(mission.id);
              }}
            >
              Follow spacecraft
            </TextButton>
            <TextButton
              onClick={() => {
                useLoader.getState().setTracked(null);
                getGlobeEngine()?.flyToAntenna(antenna.id);
              }}
            >
              View antenna
            </TextButton>
            <TextButton
              onClick={() =>
                void registerMissionTwin(mission, dataset!.network)
                  .then((assetId) => {
                    useUI.getState().select({ assetId, subsystemId: null });
                    navigate(`/twin/${encodeURIComponent(assetId)}`);
                  })
                  .catch((e: unknown) => toast('error', String(e)))
              }
            >
              Open spacecraft twin
            </TextButton>
            <TextButton onClick={() => void dive(antenna.id, createDiveDeps(navigate))}>
              Open antenna twin
            </TextButton>
          </div>
          <label className="flex gap-2 text-ink-2">
            <input
              type="checkbox"
              checked={eventFollowing}
              onChange={(e) => usePresentation.getState().setEventFollowing(e.target.checked)}
            />
            Follow acquisition/loss events
          </label>
          <StationWeather antenna={antenna} />
        </>
      )}
    </section>
  );
}
